import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of, throwError } from 'rxjs';
import { catchError, concatMap, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { BaseActionTypes } from '../../actions/base.actions';
import {
    ContractPartnerActionTypes,
    DeleteContractPartnerResponse,
    GetContractPartnersResponse, PostContractPartnerResponse,
} from '../../actions/mdqx/contract-partner.actions';
import { HttpService } from '../../../services/http.service';
import { ContractPartnerEntity } from '../../../entities/mdqx/contract-partner.entity';
import { DaveActions } from '../../actions/actions';
import { getToken } from '../../selectors/base.selectors';
import { State } from '../../index';

enum ErrorCodes {
    Get = 'Vertragspartner Abrufen fehlgeschlagen',
    Post = 'Vertragspartner Anlegen fehlgeschlagen',
    Delete = 'Vertragspartner Löschen fehlgeschlagen',
}

@Injectable()
export class ContractPartnerEffects {
    
    get$ = createEffect(() => this.actions$.pipe(
        ofType(ContractPartnerActionTypes.GetRequest),
        map(
            () => `
            {
                contractPartner {
                    ${ContractPartnerEntity.GqlFields}
                }
            }`,
        ),
        withLatestFrom(this.store.select(getToken)),
        switchMap(([query, token]) =>
            this.api.graphQl({ query }, { token }).pipe(
                map(res => res && res.contractPartner),
                concatMap(res =>
                    res
                        ? [
                              new GetContractPartnersResponse(
                                  res.map(
                                      contractPartner => new ContractPartnerEntity(contractPartner),
                                  ),
                              ),
                          ]
                        : throwError('empty response'),
                ),
                catchError((err, caught) => of(BaseActionTypes.ErrorAction({
                    Payload: {
                        ToasterMessage: ErrorCodes.Get,
                        Err: err,
                        Caught: caught,
                    }
                }))),
                ),
        ),
    ));

    
    post$ = createEffect(() => this.actions$.pipe(
        ofType(ContractPartnerActionTypes.PostRequest),
        map(
            ({ Payload }) => `
            mutation {
                createContractPartner(
                    contractId: ${JSON.stringify(Payload.contractId)}
                    customerId: ${JSON.stringify(Payload.customerId)}
                    contractGroupTypeId: ${JSON.stringify(Payload.contractGroupTypeId)}
                ) {
                    ${ContractPartnerEntity.GqlFields}
                }
            }`,
        ),
        withLatestFrom(this.store.select(getToken)),
        concatMap(([query, token]) =>
            this.api.graphQl({ query }, { token }).pipe(
                map(res => res && res.createContractPartner),
                concatMap(res =>
                    res
                        ? [new PostContractPartnerResponse(new ContractPartnerEntity(res))]
                        : throwError('empty response'),
                ),
                catchError((err, caught) => of(BaseActionTypes.ErrorAction({
                    Payload: {
                        ToasterMessage: ErrorCodes.Post,
                        Err: err,
                        Caught: caught,
                    }
                }))),
            ),
        ),
    ));

    
    delete$ = createEffect(() => this.actions$.pipe(
        ofType(ContractPartnerActionTypes.DeleteRequest),
        withLatestFrom(this.store.select(getToken)),
        concatMap(([{ Payload }, token]) =>
            this.api
                .graphQl(
                    {
                        query: `
                        mutation {
                            deleteContractPartner(id: ${JSON.stringify(Payload.id)})
                        }`,
                    },
                    { token },
                )
                .pipe(
                    map(res => res && res.deleteContractPartner),
                    concatMap(res =>
                        res
                            ? [new DeleteContractPartnerResponse(Payload.id)]
                            : throwError('falsy response'),
                    ),
                    catchError((err, caught) => of(BaseActionTypes.ErrorAction({
                        Payload: {
                            ToasterMessage: ErrorCodes.Delete,
                            Err: err,
                            Caught: caught,
                        }
                    }))),
                ),
        ),
    ));

    constructor(
        private actions$: Actions<DaveActions>,
        private api: HttpService,
        private store: Store<State>,
    ) {}
}
